import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { SupabaseService } from './supabase.service';

/**
 * Handles native deep links (Capacitor `appUrlOpen`) — the bridge that makes
 * Supabase auth work inside the wrapped app.
 *
 * On the web, Supabase auth links land in the browser and `detectSessionInUrl`
 * picks up the token from the address bar. In a native webview there IS no
 * address bar: the OS opens the app via our custom scheme (`gr.actively.app://`)
 * and hands us the full URL here. We pull the auth token/code out of it,
 * establish the session ourselves, then route the user (recovery → set a new
 * password; everything else → /auth/callback for intent-based routing).
 *
 * Pairs with SupabaseService.resetPassword(), which sends the recovery email
 * with `redirectTo = gr.actively.app://auth/callback` when running natively.
 * NB: that URL must be in Supabase → Auth → URL Configuration → Redirect URLs.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  constructor(
    private router: Router,
    private supabase: SupabaseService
  ) {}

  /** Call once at startup (AppComponent). No-op on the web. */
  init(): void {
    if (!Capacitor.isNativePlatform()) return;
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      void this.handleUrl(event.url);
    });
  }

  /** Collect params from BOTH the query string and the hash fragment. */
  private parseParams(url: string): Record<string, string> {
    const out: Record<string, string> = {};
    const hashIdx = url.indexOf('#');
    const queryIdx = url.indexOf('?');
    const collect = (s: string) =>
      new URLSearchParams(s).forEach((v, k) => (out[k] = v));
    if (hashIdx >= 0) collect(url.substring(hashIdx + 1));
    if (queryIdx >= 0) {
      const end = hashIdx >= 0 ? hashIdx : url.length;
      collect(url.substring(queryIdx + 1, end));
    }
    return out;
  }

  private async handleUrl(url: string): Promise<void> {
    try {
      const p = this.parseParams(url);

      // OAuth ran in the system browser (Browser.open) — dismiss it now that
      // the flow bounced back into the app. No-op where there's nothing to
      // close (Android Custom Tabs close themselves).
      void Browser.close().catch(() => undefined);

      // Auth error came back in the link.
      if (p['error'] || p['error_description']) {
        this.router.navigate(['/auth/login']);
        return;
      }

      let establishedSession = false;

      // PKCE flow: ?code=… → exchange for a session.
      if (p['code']) {
        const { error } = await this.supabase.client.auth.exchangeCodeForSession(
          p['code']
        );
        establishedSession = !error;
        if (error) console.error('exchangeCodeForSession failed:', error);
      } else if (p['access_token'] && p['refresh_token']) {
        // Implicit flow (recovery/magic-link): tokens in the hash → set session.
        const { error } = await this.supabase.client.auth.setSession({
          access_token: p['access_token'],
          refresh_token: p['refresh_token'],
        });
        establishedSession = !error;
        if (error) console.error('setSession from deep link failed:', error);
      } else {
        // Not an auth callback — nothing to do here for now.
        return;
      }

      if (!establishedSession) {
        this.router.navigate(['/auth/login']);
        return;
      }

      // Password recovery → the reset form; otherwise hand off to
      // /auth/callback, which consumes the stashed OAuth intent (club owner
      // vs athlete), stamps the role, and routes to the right home — same
      // path the web flow takes.
      if (p['type'] === 'recovery') {
        this.router.navigate(['/auth/update-password']);
      } else {
        this.router.navigate(['/auth/callback']);
      }
    } catch (e) {
      console.error('Deep link handling failed:', e);
      this.router.navigate(['/auth/login']);
    }
  }
}
